import { Droplets, Zap, KeyRound, Waves, Flame, Siren, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "./Reveal";
import { vaiA } from "@/lib/scroll";

const IMG = {
  idraulico:
    "https://images.unsplash.com/photo-1676210134050-6f12c6898395?crop=entropy&cs=srgb&fm=jpg&q=85",
  strumenti:
    "https://images.unsplash.com/photo-1553873002-785d775854c9?crop=entropy&cs=srgb&fm=jpg&q=85",
  elettricista:
    "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=srgb&fm=jpg&q=85",
};

const SERVIZI = [
  {
    icon: Droplets,
    titolo: "Pronto intervento idraulico",
    desc: "Perdite d'acqua, allagamenti, stasatura tubi e riparazioni di rubinetteria in urgenza.",
    badge: "Intervento rapido",
    span: "lg:col-span-7",
    img: IMG.idraulico,
    pagina: "/servizi/idraulico",
  },
  {
    icon: Zap,
    titolo: "Elettricista urgenza H24",
    desc: "Ripristino salvavita, cortocircuiti, guasti elettrici e quadri generali.",
    badge: "Disponibile ora",
    span: "lg:col-span-5",
    img: IMG.strumenti,
    pagina: "/servizi/elettricista",
  },
  {
    icon: KeyRound,
    titolo: "Fabbro e apertura porte",
    desc: "Apertura porte bloccate senza scasso, cambio serrature europee e cancelli.",
    badge: "Senza scasso",
    span: "lg:col-span-4",
    pagina: "/servizi/fabbro",
  },
  {
    icon: Waves,
    titolo: "Spurgo e autospurgo H24",
    desc: "Disostruzione colonne di scarico, pulizia fosse biologiche e videoispezioni.",
    badge: "Mezzi speciali",
    span: "lg:col-span-4",
    pagina: "/servizi/spurgo",
  },
  {
    icon: Flame,
    titolo: "Caldaie e climatizzazione",
    desc: "Assistenza blocco caldaia, assenza acqua calda e riparazione condizionatori.",
    badge: "Tecnici certificati",
    span: "lg:col-span-4",
    pagina: "/servizi/caldaie",
  },
  {
    icon: Siren,
    titolo: "Allagamenti ed emergenze complesse",
    desc: "Interventi coordinati multiservizio per condomini, aziende e abitazioni private. Squadre dedicate con mezzi attrezzati per ogni scenario, dal piccolo guasto all'emergenza strutturale.",
    badge: "H24/7",
    span: "lg:col-span-12",
    img: IMG.elettricista,
  },
];

export function Servizi() {
  return (
    <section id="servizi" className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32">
      <Reveal className="text-center">
        <p className="overline-label">I nostri servizi</p>
        <h2 className="mx-auto mt-4 max-w-2xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
          Ogni emergenza, <span className="text-amber-400">un solo numero.</span>
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-slate-400">
          Cinque squadre specializzate, una centrale operativa sempre attiva. Scopri cosa
          possiamo risolvere per te, oggi stesso.
        </p>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-12 md:gap-8">
        {SERVIZI.map((s, i) => (
          <Reveal key={s.titolo} delay={i * 0.08} className={`${s.span} md:col-span-1 lg:col-auto`}>
            <article
              data-testid={`servizio-card-${i}`}
              className={`group relative flex h-full flex-col justify-between overflow-hidden rounded-2xl border border-slate-800 bg-slatecard p-7 text-center transition-all duration-300 hover:-translate-y-1 hover:border-amber-500/50 hover:shadow-[0_20px_60px_rgba(0,0,0,0.5)] ${s.img ? "min-h-[280px]" : ""}`}
            >
              {s.img && (
                <>
                  <img
                    src={s.img}
                    alt={s.titolo}
                    className="absolute inset-0 h-full w-full object-cover opacity-25 transition-opacity duration-500 group-hover:opacity-40"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slatecard via-slatecard/70 to-transparent" />
                </>
              )}
              <div className="relative flex items-start justify-between">
                <span className="inline-flex rounded-xl bg-amber-500/15 p-3 text-amber-400">
                  <s.icon size={24} strokeWidth={1.8} />
                </span>
                <span className="rounded-full border border-amber-500/30 bg-obsidian/70 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-amber-400">
                  {s.badge}
                </span>
              </div>
              <div className="relative mt-8">
                <h3 className="font-display text-xl font-semibold text-white sm:text-2xl">
                  {s.titolo}
                </h3>
                <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-slate-400">{s.desc}</p>
                {s.pagina ? (
                  <Link
                    to={s.pagina}
                    data-testid={`servizio-cta-${i}`}
                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-400 transition-colors hover:text-amber-300"
                  >
                    Scopri il servizio
                    <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                ) : (
                  <button
                    type="button"
                    data-testid={`servizio-cta-${i}`}
                    onClick={() => vaiA("#contatti")}
                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-400 transition-colors hover:text-amber-300"
                  >
                    Richiedi assistenza
                    <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>
                )}
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
